import { CookieConsentDataSourceEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type {
	ICookieConsentServiceLocalizedInformation,
	ICookieConsentServiceOptions
} from '@inter-ikea-kompis/types';
import type { CacheStore } from '@inter-ikea-kompis/utilities';
import { GlobalCacheStore, LocaleUtility } from '@inter-ikea-kompis/utilities';

import BitboxCookieConsentService from '../../services/cookie-consent/BitboxCookieConsentService.js';
import ChinaCookieConsentService from '../../services/cookie-consent/ChinaCookieConsentService.js';
import OneWebCookieConsentService from '../../services/cookie-consent/OneWebCookieConsentService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import type ILocalizationServiceOptions from '../localization/ILocalizationServiceOptions.js';
import type ICookieConsentLevel from './types/ICookieConsentLevel.js';

/**
 * This class is used as a simple interface for cookie consent services.
 */
export default class CookieConsentService {
	private readonly locale: string;

	private localizedInformation: ICookieConsentServiceLocalizedInformation | null = null;
	private readonly dexfConfiguration: DexfConfiguration;
	private cache: CacheStore;

	/**
	 * Constructor.
	 *
	 * @param options Localization service options or a localized information object.
	 */
	constructor(options: ILocalizationServiceOptions | ICookieConsentServiceOptions) {
		if ('localizedServiceOptions' in options) {
			this.localizedInformation = options.localizedServiceOptions ?? null;
		} else {
			this.dexfConfiguration = new DexfConfiguration(options);
		}

		if ('cache' in options && options.cache) {
			this.cache = options.cache;
		} else {
			this.cache = new GlobalCacheStore();
		}

		this.locale = options.locale;
	}

	/**
	 * Checks if cookie consent meets a provided level.
	 *
	 * @param consentLevel Consent level (1-4).
	 * @returns Promise.
	 */
	public async hasCookieConsent(consentLevel: ICookieConsentLevel): Promise<boolean> {
		const localizedInformation = await this.getLocalizedInformation();

		const cookieConsentDataSource = localizedInformation.localization.cookieConsentDataSource;

		switch (cookieConsentDataSource) {
			case CookieConsentDataSourceEnum.disabled:
				return true;
			case CookieConsentDataSourceEnum.block:
				return false;
			case CookieConsentDataSourceEnum.bitbox:
				return await this.hasCookieConsentBitbox(consentLevel, localizedInformation);
			case CookieConsentDataSourceEnum.oneWeb:
				return await this.hasCookieConsentOneWeb(consentLevel, localizedInformation);
			case CookieConsentDataSourceEnum.china:
				return await this.hasCookieConsentChina(consentLevel);
			default:
				return false;
		}
	}

	private async hasCookieConsentBitbox(
		consentLevel: ICookieConsentLevel,
		localizedInformation: ICookieConsentServiceLocalizedInformation
	): Promise<boolean> {
		return await new BitboxCookieConsentService({
			endpoint: localizedInformation.endpoints.bitboxCookieConsentService,
			cache: this.cache
		}).hasCookieConsent(consentLevel);
	}

	private async hasCookieConsentChina(consentLevel: ICookieConsentLevel): Promise<boolean> {
		return await new ChinaCookieConsentService().hasCookieConsent(consentLevel);
	}

	private async hasCookieConsentOneWeb(
		consentLevel: ICookieConsentLevel,
		localizedInformation: ICookieConsentServiceLocalizedInformation
	): Promise<boolean> {
		const country = LocaleUtility.getCountryAndLanguage(this.locale).country;
		if (!country) {
			throw new ServiceException(`Locale does not contain country. Bad format: ${this.locale}`);
		}

		return await new OneWebCookieConsentService({
			endpoint: localizedInformation.endpoints.oneWebCookieConsentService,
			cache: this.cache
		}).hasCookieConsent(consentLevel, country);
	}

	/**
	 *
	 * @returns Get either the provided localized information or fetch a new/cached one using localization service options.
	 */
	private async getLocalizedInformation(): Promise<ICookieConsentServiceLocalizedInformation> {
		if (this.localizedInformation) {
			return this.localizedInformation;
		}

		const settings = await this.dexfConfiguration.getSettings();

		return {
			localization: {
				cookieConsentDataSource: settings.kompis.serviceSettings.cookieConsentDataSource
			},
			endpoints: {
				oneWebCookieConsentService: settings.kompis.endpoints.oneWebCookieConsentService,
				bitboxCookieConsentService: settings.kompis.endpoints.bitboxCookieConsentService
			}
		};
	}
}
